import { isResolvedNode } from './is-resolved-sankey-node.js';
import type {
  SankeyLayoutLink,
  SankeyLayoutNode,
} from '../types/sankey-internal.js';

/** The transitive data flow reachable from a node: its related nodes and every link crossed while collecting them. */
export interface SankeyRelatedFlow {
  nodes: SankeyLayoutNode[];
  links: SankeyLayoutLink[];
}

interface FlowStep {
  node: SankeyLayoutNode;
  link: SankeyLayoutLink;
}

const getUpstreamSteps = (node: SankeyLayoutNode): FlowStep[] =>
  (node.targetLinks ?? []).flatMap((link) =>
    isResolvedNode(link.source) ? [{ node: link.source, link }] : [],
  );

const getDownstreamSteps = (node: SankeyLayoutNode): FlowStep[] =>
  (node.sourceLinks ?? []).flatMap((link) =>
    isResolvedNode(link.target) ? [{ node: link.target, link }] : [],
  );

function collectFlow(
  start: SankeyLayoutNode,
  getNextSteps: (node: SankeyLayoutNode) => FlowStep[],
): SankeyRelatedFlow {
  const nodes = new Set<SankeyLayoutNode>();
  const links = new Set<SankeyLayoutLink>();
  const queue = [start];

  for (const current of queue) {
    for (const step of getNextSteps(current)) {
      links.add(step.link);
      if (!nodes.has(step.node)) {
        nodes.add(step.node);
        queue.push(step.node);
      }
    }
  }

  return { nodes: [...nodes], links: [...links] };
}

/**
 * Traverses the full data flow of `node`: every node feeding into it
 * (following `targetLinks` upstream) and every node it feeds into (following
 * `sourceLinks` downstream), transitively, plus every link crossed while
 * doing so, including links between two related nodes that never touch
 * `node` itself. `node` itself is excluded from `nodes`.
 *
 * The two directions are walked separately on purpose. A single undirected
 * traversal would step back down from an ancestor into that ancestor's other
 * branches and end up returning the whole connected component, pulling in
 * siblings that neither feed the hovered node nor are fed by it.
 */
export function getRelatedSankeyFlow(
  node: SankeyLayoutNode,
): SankeyRelatedFlow {
  const upstream = collectFlow(node, getUpstreamSteps);
  const downstream = collectFlow(node, getDownstreamSteps);

  const nodes = new Set([...upstream.nodes, ...downstream.nodes]);
  nodes.delete(node);

  const links = new Set([...upstream.links, ...downstream.links]);

  return { nodes: [...nodes], links: [...links] };
}
